import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Paperclip, Send, StopCircle, Wrench, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "@/api/client";
import { toastApiError } from "@/api/errors";
import type {
  Attachment,
  ExecutionStep,
  Message,
  MessageAttachment,
  Thread,
  ThreadDetail,
} from "@/api/types";
import { tokenStorage } from "@/auth/tokenStorage";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAllowedModels } from "@/features/chat/hooks/useAllowedModels";
import { PiiBadge } from "@/features/pii/components/PiiBadge";
import { AttachmentBubble } from "@/features/threads/components/AttachmentBubble";
import { DelegationBlock } from "@/features/threads/components/DelegationBlock";
import { HitlBanner } from "@/features/threads/components/HitlBanner";
import { MessageBubble } from "@/features/threads/components/MessageBubble";
import { ThinkingBlock } from "@/features/threads/components/ThinkingBlock";
import { ToolCallBlock } from "@/features/threads/components/ToolCallBlock";
import {
  extractAttachmentIdsFromPreview,
  ToolResultBlock,
} from "@/features/threads/components/ToolResultBlock";
import { uploadAttachmentXhr } from "@/features/threads/hooks/useAttachments";
import {
  useThreadMessages,
  useThreadSteps,
} from "@/features/threads/hooks/useThreads";
import { openSseStream } from "@/lib/sseClient";
import { formatBytes, validateFile } from "@/lib/upload";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/stores/uiStore";

interface Props {
  threadId: string;
  /**
   * URL custom para POST (default: /threads/{threadId}/messages).
   * Sandbox usa /agents/{id}/sandbox/messages.
   */
  postPath?: string;
  /**
   * Callback opcional chamado para cada evento SSE recebido. Roda
   * **alem** do handler interno (que cuida do render). Usado pelo
   * team chat para alimentar o ``<TeamDiagram>`` em paralelo.
   */
  onSseEvent?: (ev: { event: string; data: unknown }) => void;
  /**
   * Modo draft 1:1: quando ``threadId`` vem vazio, cria a thread no
   * primeiro send() com ``POST /threads { type: "direct", agent_id }``.
   * Permite chat com bot sem persistir thread fantasma se o usuario
   * fechar antes de enviar.
   */
  draftAgentId?: string;
  /**
   * Modo draft team: idem, mas usa ``type: "team", team_session_id``.
   */
  draftSessionId?: string;
  /**
   * Callback disparado quando a thread foi criada com sucesso (no draft
   * mode). O parent pode entao navegar para ``/threads/:id`` ou atualizar
   * estado interno.
   */
  onThreadCreated?: (threadId: string) => void;
  /**
   * Mensagem que sera enviada automaticamente uma unica vez no mount.
   * Usado pelo /chat (M8): user digita primeira msg na tela vazia, cria
   * thread, navega para /chat/:id e o ChatStream auto-envia o texto.
   * Se vazio/undefined, nao faz nada.
   */
  autoSubmit?: string;
  /**
   * M8: conteudo extra renderizado a ESQUERDA dos botoes do composer
   * (anexo / send). Usado pelo /chat para encaixar o ``<ModelSelector>``
   * inline no composer, em vez de um header dedicado.
   */
  composerLeftSlot?: React.ReactNode;
}

/**
 * Bloco renderizado: pode vir do historico persistido (messages +
 * execution_steps) OU da stream em tempo real (``liveBlocks``).
 * Exportado para o ``ThreadTranscript`` (read-only, ex.: detalhe de
 * task em background).
 */
export interface RenderBlock {
  key: string;
  kind:
    | "bubble"
    | "thinking"
    | "tool_call"
    | "tool_result"
    | "hitl"
    | "delegation"
    | "pii"
    | "system";
  role?: "user" | "assistant" | "system_event";
  /**
   * Em team mode, identifica o autor da bolha/bloco: ``null`` ou
   * ``undefined`` = supervisor; um slug = subagent (renderiza com label
   * "@<slug>" no topo do balao para diferenciar visualmente).
   */
  agentSlug?: string | null;
  content?: string;
  toolName?: string;
  args?: Record<string, unknown>;
  outputPreview?: string;
  hitlRequestId?: string;
  toolInput?: Record<string, unknown>;
  /** HITL: ja decidido. Quando true, banner mostra badge + comment. */
  hitlResolved?: boolean;
  hitlDecision?: "approve" | "reject";
  hitlComment?: string;
  /** Delegation block (team mode / M6). */
  toSlug?: string | null;
  taskDescription?: string | null;
  /** PII detection block (M7 / Fase 11). */
  piiDirection?: "input" | "output";
  piiCount?: number;
  piiCategories?: string[];
  streaming?: boolean;
  /** Ordenacao cronologica do historico. */
  createdAt?: string;
  /** Anexos vinculados a Message (renderizados no MessageBubble). M3. */
  attachments?: MessageAttachment[];
  /** IDs de anexos do tool_result (ToolResultBlock). M3. */
  attachmentIds?: string[];
  /**
   * M8: snapshot do ``llm_model_id`` que respondeu esta bolha
   * (chat direto). Resolvido para display_name em ``renderBlock``
   * via ``modelLabels`` antes de passar pro MessageBubble.
   */
  llmModelId?: string;
}

/**
 * Orquestrador do chat. Carrega historico (mensagens + execution_steps)
 * interleavados por timestamp + abre SSE em ``send()`` + processa
 * eventos. Durante o streaming, blocos vivos sao acumulados em
 * ``liveBlocks``; ao final, refetcha os persistidos e limpa o live.
 *
 * **HITL**: quando o backend emite ``hitl_requested``, renderiza
 * ``<HitlBanner>`` inline. O banner chama ``decideHitl(...)`` -- que
 * por sua vez **abre um SSE novo** contra ``/hitl-requests/{id}/decide``
 * e roteia os eventos resultantes pelo mesmo ``handleEvent``. Assim
 * ``hitl_resolved`` + ``token`` + ``message_completed`` continuam o
 * fluxo na mesma UI sem precisar reload.
 */
export function ChatStream({
  threadId,
  postPath,
  onSseEvent,
  draftAgentId,
  draftSessionId,
  onThreadCreated,
  autoSubmit,
  composerLeftSlot,
}: Props) {
  // Ref estavel: handler interno chama o onSseEvent atual sem requerer
  // remontagem de listeners por mudanca de prop.
  const onSseEventRef = useRef(onSseEvent);
  useEffect(() => {
    onSseEventRef.current = onSseEvent;
  }, [onSseEvent]);
  // No draft mode, o threadId real so existe apos o primeiro send(). Antes
  // disso usamos uma string vazia para desabilitar as queries de historico
  // (que veem ``enabled: Boolean(threadId)``).
  const [createdThreadId, setCreatedThreadId] = useState<string | null>(null);
  const effectiveThreadId = createdThreadId ?? threadId;
  const qc = useQueryClient();
  const [liveBlocks, setLiveBlocks] = useState<RenderBlock[]>([]);
  const [streaming, setStreaming] = useState(false);
  // Flag setada quando ``ensureThreadId`` cria a thread localmente.
  // Indica que estamos no PRIMEIRO stream pos-criacao -- enquanto isso
  // o usuario deve ver apenas os liveBlocks (a optimistic user msg +
  // tokens em streaming), NUNCA o historico persistido.
  //
  // Por que: o backend persiste a user msg assim que recebe o POST. Se
  // a query de mensagens roda durante esse stream e traz user+assistant
  // do backend, render mostra esses + os liveBlocks ao mesmo tempo =
  // duplicacao visivel da 1a interacao inteira.
  //
  // Em vez de tentar suprimir a query (que entra em deadlock com o
  // useEffect que precisa de ``data`` fresh pra limpar liveBlocks), a
  // query roda em background normalmente, mas o render esconde o
  // historico atraves de ``visibleHistoryBlocks`` abaixo. A troca
  // liveBlocks -> history acontece num unico render quando a flag
  // cai (junto com setLiveBlocks([])) -- atomico, sem flash de
  // duplicacao.
  //
  // Limpa no useEffect que sweep dos liveBlocks (apos o stream encerrar
  // e o historico estar populado).
  const [firstStreamPostCreate, setFirstStreamPostCreate] = useState(false);
  const msgsQuery = useThreadMessages(effectiveThreadId);
  const stepsQuery = useThreadSteps(effectiveThreadId);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Em team mode o supervisor pode delegar a VARIOS subagentes em paralelo
  // -> os chunks SSE intercalam por agente. Mantemos UMA bolha (e UM bloco
  // thinking) ABERTA POR ``agent_slug`` (null = supervisor), roteando cada
  // chunk pelo seu slug. Antes havia um unico "ref atual" que era fechado a
  // cada flip de slug -> gerava dezenas de baloes quebrados alternando.
  // Mapa: agent_slug -> key do bloco aberto daquele agente.
  const openBubbleBySlug = useRef<Map<string | null, string>>(new Map());
  const openThinkingBySlug = useRef<Map<string | null, string>>(new Map());
  // Trackeia o ultimo tool_call key emitido (live), para que ao chegar
  // ``hitl_requested`` possamos remove-lo (a tool nao executou).
  const lastToolCallKeyRef = useRef<string | null>(null);
  // Set de hitlRequestIds ja decididos -- usado para desabilitar o
  // banner enquanto o SSE de decide nao chega.
  const pendingDecideRef = useRef<Set<string>>(new Set());
  // Flag setada por finalizeStream quando um stream termina. O effect
  // abaixo limpa ``liveBlocks`` quando o historico estiver fresco (
  // ``msgsQuery.data`` populado e ``!isFetching``). Sem este passo:
  // 1. Em draft mode, ``suppressHistoryFetch`` desabilita a query
  //    durante o streaming.
  // 2. Quando o "done" SSE evento dispara setStreaming(false), o React
  //    re-renderiza com ``msgsQuery`` apontando para a thread real
  //    (chave T) e auto-fetch comeca.
  // 3. Porem o ``onClose`` callback do SSE library pode rodar ANTES da
  //    re-render commit -- entao chamar refetch() ali opera sobre o
  //    queryObject antigo (chave vazia), falha silenciosamente, e
  //    setLiveBlocks([]) nunca roda --> history + live sobrepoem.
  // O effect resolve isso esperando o estado React assentar antes de
  // limpar.
  const justFinishedRef = useRef(false);

  // Auto-scroll inteligente: cola no fundo apenas se ja esta perto.
  const stickToBottomRef = useRef(true);
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 100;
  };

  // Historico persistido (interleavado por created_at).
  const historyBlocks = useMemo<RenderBlock[]>(() => {
    const messages = msgsQuery.data ?? [];
    const steps = stepsQuery.data?.items ?? [];
    return buildHistoryBlocks(messages, steps);
  }, [msgsQuery.data, stepsQuery.data]);

  // M8: mapa llm_model_id -> "provider · display_name" para renderizar
  // chip na bolha assistant em chat direto. Em direct/team mode o mapa
  // tambem vai existir (hook eh global pro user) mas as bolhas nao
  // tem ``llmModelId`` set, entao o chip nao aparece.
  const allowedModels = useAllowedModels();
  const modelLabels = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const m of allowedModels.data ?? []) {
      map.set(m.id, `${m.provider} · ${m.display_name}`);
    }
    return map;
  }, [allowedModels.data]);

  useEffect(() => {
    if (stickToBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [historyBlocks, liveBlocks]);

  /** Fecha (streaming=false) a bolha assistant ABERTA de UM agente. */
  function closeAgentBubble(slug: string | null) {
    const cur = openBubbleBySlug.current.get(slug);
    if (!cur) return;
    openBubbleBySlug.current.delete(slug);
    setLiveBlocks((prev) =>
      prev.map((b) => (b.key === cur ? { ...b, streaming: false } : b)),
    );
  }

  /** Fecha o bloco thinking ABERTO de UM agente. */
  function closeAgentThinking(slug: string | null) {
    const cur = openThinkingBySlug.current.get(slug);
    if (!cur) return;
    openThinkingBySlug.current.delete(slug);
    setLiveBlocks((prev) =>
      prev.map((b) => (b.key === cur ? { ...b, streaming: false } : b)),
    );
  }

  /**
   * Fecha bolha + thinking de UM agente -- usado antes do tool_call/
   * tool_result DELE (preserva a ordem texto->tool->texto por agente)
   * sem mexer nos outros agentes que estao streamando em paralelo.
   */
  function closeAgentSegment(slug: string | null) {
    closeAgentBubble(slug);
    closeAgentThinking(slug);
  }

  /**
   * Fecha TODOS os blocos abertos (fim de turno: message_completed/done/
   * error/cancelled/hitl). Marca qualquer bloco ainda streaming como
   * concluido e zera os mapas por slug.
   */
  function closeAllCurrent() {
    if (
      openBubbleBySlug.current.size === 0 &&
      openThinkingBySlug.current.size === 0
    ) {
      return;
    }
    openBubbleBySlug.current.clear();
    openThinkingBySlug.current.clear();
    setLiveBlocks((prev) =>
      prev.map((b) => (b.streaming ? { ...b, streaming: false } : b)),
    );
  }

  /**
   * Acrescenta ``text`` na bolha assistant ABERTA do agente ``agentSlug``
   * (null = supervisor). Cria a bolha se for o 1o chunk daquele agente.
   * Em team mode com subagentes paralelos, cada agente tem SUA bolha viva
   * -- chunks intercalados de A e B vao cada um para a bolha certa, sem
   * quebrar. Fecha o thinking DESSE agente (texto e raciocinio em blocos
   * separados consecutivos).
   */
  function appendAssistantToken(text: string, agentSlug?: string | null) {
    if (!text) return;
    const slug = agentSlug ?? null;
    closeAgentThinking(slug);
    const cur = openBubbleBySlug.current.get(slug);
    if (!cur) {
      const key = `asst-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      openBubbleBySlug.current.set(slug, key);
      setLiveBlocks((prev) => [
        ...prev,
        {
          key,
          kind: "bubble",
          role: "assistant",
          agentSlug: slug,
          content: text,
          streaming: true,
        },
      ]);
    } else {
      setLiveBlocks((prev) =>
        prev.map((b) =>
          b.key === cur ? { ...b, content: (b.content ?? "") + text } : b,
        ),
      );
    }
  }

  /**
   * Acrescenta ``text`` no bloco thinking ABERTO do agente ``agentSlug``.
   * Fecha a bolha de texto DESSE agente (raciocinio e resposta separados).
   */
  function appendThinkingChunk(text: string, agentSlug?: string | null) {
    if (!text) return;
    const slug = agentSlug ?? null;
    closeAgentBubble(slug);
    const cur = openThinkingBySlug.current.get(slug);
    if (!cur) {
      const key = `thk-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      openThinkingBySlug.current.set(slug, key);
      setLiveBlocks((prev) => [
        ...prev,
        {
          key,
          kind: "thinking",
          agentSlug: slug,
          content: text,
          streaming: true,
        },
      ]);
    } else {
      setLiveBlocks((prev) =>
        prev.map((b) =>
          b.key === cur ? { ...b, content: (b.content ?? "") + text } : b,
        ),
      );
    }
  }

  /**
   * Trata uma queda INESPERADA de conexao (erro de rede no SSE, sem o
   * evento ``done``). Sem o LangGraph Agent Server nao ha "resume" do run
   * do servidor, e re-buscar o historico no meio do stream duplicaria a
   * bolha do usuario (optimistic + persistida). Entao: para o streaming,
   * PRESERVA o que ja apareceu (nao limpa ``liveBlocks``) e sinaliza inline
   * que a resposta pode estar incompleta. O usuario recarrega (le o
   * historico persistido) ou reenvia. Espelha o padrao do evento
   * ``cancelled``.
   */
  function markDisconnected() {
    closeAllCurrent();
    setStreaming(false);
    lastToolCallKeyRef.current = null;
    setLiveBlocks((prev) => [
      ...prev,
      {
        key: `sys-disc-${Date.now()}`,
        kind: "system",
        role: "system_event",
        content:
          "*Conexão perdida. A resposta pode estar incompleta — recarregue a página ou reenvie a mensagem.*",
      },
    ]);
    toast.error("Conexão perdida durante a resposta.");
  }

  function handleEvent(ev: { event: string; data: unknown }) {
    // Forwarding opcional para componentes externos (team diagram).
    try {
      onSseEventRef.current?.(ev);
    } catch (err) {
      // Nao deixa o consumer derrubar o handler interno.
      console.error("onSseEvent consumer threw", err);
    }
    const data = (ev.data ?? {}) as Record<string, unknown>;
    switch (ev.event) {
      case "token":
        appendAssistantToken(
          String(data.content ?? ""),
          typeof data.agent_slug === "string" ? data.agent_slug : null,
        );
        break;
      case "thinking":
        appendThinkingChunk(
          String(data.content ?? ""),
          typeof data.agent_slug === "string" ? data.agent_slug : null,
        );
        break;
      case "tool_call": {
        // Fecha SO o segmento do agente que chamou a tool (team mode:
        // agent_slug), preservando os outros subagentes em paralelo.
        closeAgentSegment(
          typeof data.agent_slug === "string" ? data.agent_slug : null,
        );
        const key = `tc-${String(data.tool_call_id ?? Math.random())}`;
        lastToolCallKeyRef.current = key;
        setLiveBlocks((prev) => [
          ...prev,
          {
            key,
            kind: "tool_call",
            toolName: String(data.tool_name ?? ""),
            args: (data.args as Record<string, unknown>) ?? {},
          },
        ]);
        break;
      }
      case "tool_result": {
        // Fecha SO o segmento do agente desta tool (team mode).
        closeAgentSegment(
          typeof data.agent_slug === "string" ? data.agent_slug : null,
        );
        lastToolCallKeyRef.current = null;
        setLiveBlocks((prev) => [
          ...prev,
          {
            key: `tr-${String(data.tool_call_id ?? Math.random())}`,
            kind: "tool_result",
            outputPreview: String(data.output_preview ?? ""),
          },
        ]);
        break;
      }
      case "pii_detected": {
        const direction = String(data.direction ?? "input") as
          | "input"
          | "output";
        const count = Number(data.count ?? 0);
        const categories = Array.isArray(data.categories)
          ? (data.categories as string[])
          : [];
        setLiveBlocks((prev) => [
          ...prev,
          {
            key: `pii-${direction}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            kind: "pii",
            piiDirection: direction,
            piiCount: count,
            piiCategories: categories,
          },
        ]);
        break;
      }
      case "delegation": {
        // A delegacao parte do supervisor -> fecha so o balao dele,
        // preservando subagentes que ja estejam streamando em paralelo.
        closeAgentSegment(null);
        lastToolCallKeyRef.current = null;
        setLiveBlocks((prev) => [
          ...prev,
          {
            key: `delg-${String(data.delegation_id ?? Math.random())}`,
            kind: "delegation",
            toSlug: typeof data.to_slug === "string" ? data.to_slug : null,
            taskDescription:
              typeof data.task_description === "string"
                ? data.task_description
                : null,
          },
        ]);
        break;
      }
      case "hitl_requested": {
        closeAllCurrent();
        // Remove o tool_call que acabou de ser emitido (a tool nao
        // chegou a executar -- o wrapper raise interrupt). Sem essa
        // remocao, a UI mostra o tool_call duplicado pois o resume
        // vai gerar OUTRO on_tool_start quando a tool rodar de fato.
        const tcKeyToDrop = lastToolCallKeyRef.current;
        lastToolCallKeyRef.current = null;
        setLiveBlocks((prev) => {
          const filtered = tcKeyToDrop
            ? prev.filter((b) => b.key !== tcKeyToDrop)
            : prev;
          return [
            ...filtered,
            {
              key: `hitl-${String(data.hitl_request_id ?? "")}`,
              kind: "hitl",
              hitlRequestId: String(data.hitl_request_id ?? ""),
              toolName: String(data.tool_name ?? ""),
              toolInput: (data.tool_input as Record<string, unknown>) ?? {},
            },
          ];
        });
        break;
      }
      case "hitl_resolved": {
        const id = String(data.hitl_request_id ?? "");
        const dec = String(data.decision ?? "approve") as "approve" | "reject";
        const cmt = typeof data.comment === "string" ? data.comment : undefined;
        pendingDecideRef.current.delete(id);
        // Mantem o banner visivel mas marca como resolvido -- o user
        // ve o registro historico da decisao em vez do banner sumir.
        setLiveBlocks((prev) =>
          prev.map((b) =>
            b.hitlRequestId === id
              ? {
                  ...b,
                  hitlResolved: true,
                  hitlDecision: dec,
                  hitlComment: cmt,
                }
              : b,
          ),
        );
        break;
      }
      case "message_completed": {
        // ``message_completed`` carrega o content final consolidado do
        // SUPERVISOR (agent_id NULL). Os subagentes ja foram flushados como
        // segmentos proprios (cada um virou mensagem com seu agent_id) e
        // suas bolhas vivas seguem ate o fim do turno. Aqui finalizamos so
        // a bolha do supervisor (slug null) -- garante o texto correto
        // mesmo se algum chunk se perdeu no fio.
        const finalContent =
          typeof data.content === "string" ? data.content : "";
        const cur = openBubbleBySlug.current.get(null);
        if (finalContent) {
          if (cur) {
            setLiveBlocks((prev) =>
              prev.map((b) =>
                b.key === cur
                  ? { ...b, content: finalContent, streaming: false }
                  : b,
              ),
            );
          } else {
            setLiveBlocks((prev) => [
              ...prev,
              {
                key: `asst-final-${Date.now()}`,
                kind: "bubble",
                role: "assistant",
                content: finalContent,
              },
            ]);
          }
        }
        // Remove SO o supervisor dos mapas (subagentes seguem ate done).
        closeAgentSegment(null);
        break;
      }
      case "thread_titled": {
        // Backend gerou o título automático (1a mensagem). Atualiza o
        // cache da thread (header) na hora e revalida a lista lateral.
        const tid =
          (typeof data.thread_id === "string" && data.thread_id) ||
          effectiveThreadId;
        const title = typeof data.title === "string" ? data.title : "";
        if (tid && title) {
          qc.setQueryData<ThreadDetail | undefined>(["thread", tid], (old) =>
            old ? { ...old, title } : old,
          );
          void qc.invalidateQueries({ queryKey: ["threads"] });
        }
        break;
      }
      case "cancelled":
        closeAllCurrent();
        setLiveBlocks((prev) => [
          ...prev,
          {
            key: `sys-${Date.now()}`,
            kind: "system",
            role: "system_event",
            content: "*Conversa cancelada pelo usuário.*",
          },
        ]);
        break;
      case "error":
        toast.error(String(data.message ?? "Erro no stream"));
        break;
      case "done":
        // Fim do turno: fecha QUALQUER segmento ainda aberto (ex.: a ultima
        // bolha de um subagente que nao terminou em tool boundary), para nao
        // deixar spinner pendurado ate o refetch do historico trocar os
        // liveBlocks pelos blocos persistidos.
        closeAllCurrent();
        setStreaming(false);
        break;
      default:
        break;
    }
  }

  const finalizeStream = useCallback(() => {
    setStreaming(false);
    openBubbleBySlug.current.clear();
    openThinkingBySlug.current.clear();
    lastToolCallKeyRef.current = null;
    // Marca pra que o effect (abaixo) limpe ``liveBlocks`` assim que
    // a query de historico tiver carregado os dados frescos.
    justFinishedRef.current = true;
    // Invalida explicitamente: sem isto, a query de mensagens (que ja
    // estava ``enabled`` em thread existente) nao refetcha e a bolha
    // historica nao traz os ``Message.attachments`` recem-amarrados.
    // Mesmo para drafts -- garantir refetch evita janela de race.
    const tid = createdThreadId ?? threadId;
    if (tid) {
      void qc.invalidateQueries({ queryKey: ["thread-messages", tid] });
      void qc.invalidateQueries({ queryKey: ["thread-steps", tid] });
      void qc.invalidateQueries({ queryKey: ["thread-attachments", tid] });
      // Garante que o título automático (evento ``thread_titled``) apareça
      // mesmo se o evento SSE não tiver sido processado a tempo.
      void qc.invalidateQueries({ queryKey: ["thread", tid] });
    }
    void qc.invalidateQueries({ queryKey: ["threads"] });
  }, [qc, createdThreadId, threadId]);

  // Limpa liveBlocks assim que o historico estiver fresco apos o
  // stream terminar. Le ``msgsQuery``/``stepsQuery`` deste render pra
  // sempre ver dados atuais (sem closure stale como acontecia no
  // finalizeStream anterior).
  useEffect(() => {
    if (!justFinishedRef.current) return;
    if (streaming) return;
    // Pendencia HITL nao resolvida fica em liveBlocks ate o decide
    // emitir hitl_resolved + done. Nao limpa enquanto isso.
    const hasPendingHitl = liveBlocks.some(
      (b) => b.kind === "hitl" && !b.hitlResolved,
    );
    if (hasPendingHitl) return;
    if (msgsQuery.isFetching || stepsQuery.isFetching) return;
    if (!msgsQuery.data || !stepsQuery.data) return;
    if (msgsQuery.isError || stepsQuery.isError) return;
    setLiveBlocks([]);
    // Encerra o estado especial "pos-criacao". Proximos streams (2a
    // msg em diante) operam no modo normal: a query do historico fica
    // sempre habilitada e a optimistic so se mistura com o historico
    // por um curtissimo intervalo apos ``setLiveBlocks([])``.
    setFirstStreamPostCreate(false);
    justFinishedRef.current = false;
  }, [
    streaming,
    msgsQuery.data,
    msgsQuery.isFetching,
    msgsQuery.isError,
    stepsQuery.data,
    stepsQuery.isFetching,
    stepsQuery.isError,
    liveBlocks,
  ]);

  // Cleanup: aborta o SSE em andamento ao desmontar (ex.: navegar para
  // outra thread/pagina no meio do stream). Sem isto, o ``fetchEventSource``
  // segue rodando em background e seus handlers disparam em um componente
  // ja desmontado.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  /**
   * Abre o SSE de decide(). Reutiliza ``handleEvent`` para que os
   * eventos do resume (hitl_resolved, token, message_completed, done)
   * caiam no mesmo fluxo da UI.
   */
  const decideHitl = useCallback(
    async (
      hitlRequestId: string,
      payload: {
        decision: "approve" | "reject";
        comment?: string;
        tool_input_override?: Record<string, unknown>;
      },
    ) => {
      // Idempotencia: nao abre duas decides para o mesmo hitl.
      if (pendingDecideRef.current.has(hitlRequestId)) return;
      pendingDecideRef.current.add(hitlRequestId);
      setStreaming(true);
      openBubbleBySlug.current.clear();
      openThinkingBySlug.current.clear();

      const token = await tokenStorage.getToken();
      await new Promise<void>((resolve) => {
        abortRef.current = openSseStream(
          {
            path: `/hitl-requests/${hitlRequestId}/decide`,
            method: "POST",
            body: payload,
          },
          {
            token,
            onEvent: handleEvent,
            onClose: () => {
              pendingDecideRef.current.delete(hitlRequestId);
              finalizeStream();
              resolve();
            },
            onError: (err) => {
              console.error("SSE error (decide)", err);
              pendingDecideRef.current.delete(hitlRequestId);
              markDisconnected();
              resolve();
            },
          },
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [finalizeStream],
  );

  // Em draft mode, materializa a thread no servidor. Cria so na
  // primeira chamada -- subsequentes retornam o id ja criado. Usado
  // tanto pelo ``send`` (enviar mensagem) quanto pelo composer
  // (anexar arquivo antes da primeira mensagem). Sem isto, o clipe
  // ficaria desabilitado em ``/threads/new`` ate o user enviar texto.
  const ensureThreadId = useCallback(async (): Promise<string | null> => {
    const existing = createdThreadId ?? threadId;
    if (existing) return existing;
    if (!draftAgentId && !draftSessionId) return null;
    const created = await api.POST<Thread>(
      "/threads",
      draftAgentId
        ? { type: "direct", agent_id: draftAgentId }
        : { type: "team", team_session_id: draftSessionId },
    );
    setCreatedThreadId(created.id);
    // Marca que o proximo stream eh "pos-criacao" -- usado pelo
    // ``suppressHistoryFetch`` pra evitar dupla renderizacao da 1a
    // user msg (optimistic + historico). Limpa no useEffect que
    // limpa ``liveBlocks`` no fim do stream.
    setFirstStreamPostCreate(true);
    onThreadCreated?.(created.id);
    return created.id;
  }, [
    createdThreadId,
    threadId,
    draftAgentId,
    draftSessionId,
    onThreadCreated,
  ]);

  const send = useCallback(
    (
      content: string,
      attachmentIds: string[] = [],
      attachmentsPreview: MessageAttachment[] = [],
    ) => {
      if (streaming) return;
      const trimmed = content.trim();
      if (!trimmed) return;
      setStreaming(true);
      openBubbleBySlug.current.clear();
      openThinkingBySlug.current.clear();
      lastToolCallKeyRef.current = null;

      // Otimistic: bolha do usuario com snapshot dos anexos. Sem isso,
      // o usuario ve a propria mensagem sem o arquivo enquanto o
      // agente processa -- visualmente confuso. Depois do stream
      // terminar, o refetch do historico substitui pelo Message.attachments
      // do backend.
      setLiveBlocks((prev) => [
        ...prev,
        {
          key: `user-${Date.now()}`,
          kind: "bubble",
          role: "user",
          content: trimmed,
          attachments:
            attachmentsPreview.length > 0 ? attachmentsPreview : undefined,
        },
      ]);

      void (async () => {
        try {
          // Em draft mode, materializa thread no servidor. ``ensureThreadId``
          // eh idempotente -- se ja foi criada pelo upload de anexo, retorna
          // o mesmo id.
          const activeThreadId = await ensureThreadId();

          if (!activeThreadId && !postPath) {
            // Sem threadId e sem postPath custom: nao temos para onde
            // enviar. Falha defensiva.
            setStreaming(false);
            toast.error("Não foi possível iniciar a conversa.");
            return;
          }

          const path = postPath ?? `/threads/${activeThreadId}/messages`;
          const token = await tokenStorage.getToken();
          const body: { content: string; attachment_ids?: string[] } = {
            content: trimmed,
          };
          if (attachmentIds.length > 0) {
            body.attachment_ids = attachmentIds;
          }
          abortRef.current = openSseStream(
            { path, method: "POST", body },
            {
              token,
              onEvent: handleEvent,
              onClose: finalizeStream,
              onError: (err) => {
                console.error("SSE error", err);
                markDisconnected();
              },
            },
          );
        } catch (err) {
          console.error("Falha ao iniciar conversa", err);
          setStreaming(false);
          // Surface da mensagem do backend (ex.: 409 de agente em rascunho
          // -> "Publique o agente para iniciar conversas."), com fallback
          // generico para erros sem corpo de API.
          toastApiError(err);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [streaming, effectiveThreadId, postPath, finalizeStream, ensureThreadId],
  );

  function cancel() {
    abortRef.current?.abort();
    setStreaming(false);
    openBubbleBySlug.current.clear();
    openThinkingBySlug.current.clear();
    lastToolCallKeyRef.current = null;
  }

  // M8: auto-envia a primeira mensagem uma unica vez no mount. Usado
  // pelo /chat quando o user digita a primeira msg na tela vazia, a
  // pagina cria a thread + navega para /chat/:id passando o texto via
  // ``autoSubmit``. Ref garante idempotencia caso o re-render dispare.
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (autoSubmittedRef.current) return;
    if (!autoSubmit) return;
    if (!threadId) return; // espera a thread existir
    autoSubmittedRef.current = true;
    send(autoSubmit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit, threadId]);

  // Durante o PRIMEIRO stream pos-criacao, escondemos o historico do
  // render -- o backend ja persistiu a user msg (e em seguida o
  // assistant) e o ``msgsQuery`` traz isso em paralelo aos liveBlocks
  // que estamos renderizando otimisticamente. Sem essa mascara, o
  // usuario via ambos = duplicacao visivel.
  //
  // A troca acontece atomicamente no useEffect que limpa ``liveBlocks``
  // E ``firstStreamPostCreate`` no mesmo render: liveBlocks -> [],
  // visibleHistoryBlocks -> historyBlocks. Sem flash intermediario.
  const visibleHistoryBlocks = firstStreamPostCreate ? [] : historyBlocks;

  return (
    <ChatStreamView
      scrollRef={scrollRef}
      onScroll={handleScroll}
      historyBlocks={visibleHistoryBlocks}
      liveBlocks={liveBlocks}
      streaming={streaming}
      send={send}
      cancel={cancel}
      decideHitl={decideHitl}
      composerThreadId={effectiveThreadId || undefined}
      ensureThreadId={ensureThreadId}
      modelLabels={modelLabels}
      composerLeftSlot={composerLeftSlot}
    />
  );
}

/**
 * Constroi um array de RenderBlock interleavando ``messages`` e
 * execution_steps tipo tool_call/tool_result/hitl_*, ordenando por
 * ``created_at``.
 */
export function buildHistoryBlocks(
  messages: Message[],
  steps: ExecutionStep[],
): RenderBlock[] {
  // Map auxiliar: hitl_request_id -> {decision, comment} a partir dos
  // steps ``hitl_resolved``. Usado para marcar os blocos de banner como
  // resolvidos quando reconstruimos o historico.
  const resolved = new Map<
    string,
    { decision: "approve" | "reject"; comment?: string }
  >();
  for (const s of steps) {
    if (s.step_type === "hitl_resolved") {
      const payload = s.payload as {
        hitl_request_id?: string;
        decision?: string;
        comment?: string;
      };
      if (payload.hitl_request_id) {
        resolved.set(payload.hitl_request_id, {
          decision: payload.decision === "reject" ? "reject" : "approve",
          comment: payload.comment,
        });
      }
    }
  }

  const blocks: RenderBlock[] = [];
  for (const m of messages) {
    blocks.push({
      key: `msg-${m.id}`,
      kind: "bubble",
      role: m.role,
      content: m.content,
      createdAt: m.created_at,
      attachments: m.attachments,
      // Team mode: slug do subagente que produziu a mensagem (persistido
      // como mensagem propria por subagente). Reconstroi o header
      // @subagente no reload. null no supervisor / user / system_event.
      agentSlug: m.agent_slug ?? null,
      // M8: snapshot do modelo que respondeu (so populado em chat
      // direto; null/undefined em direct/team).
      llmModelId: m.llm_model_id ?? undefined,
    });
  }
  for (const s of steps) {
    if (s.step_type === "tool_call") {
      const payload = s.payload as {
        tool_name?: string;
        args?: Record<string, unknown>;
      };
      blocks.push({
        key: `step-tc-${s.id}`,
        kind: "tool_call",
        toolName: payload.tool_name ?? "",
        args: payload.args ?? {},
        createdAt: s.created_at,
      });
    } else if (s.step_type === "tool_result") {
      const payload = s.payload as {
        preview?: string;
      };
      blocks.push({
        key: `step-tr-${s.id}`,
        kind: "tool_result",
        outputPreview: payload.preview ?? "",
        createdAt: s.created_at,
      });
    } else if (s.step_type === "hitl_requested") {
      const payload = s.payload as {
        tool_name?: string;
        tool_input?: Record<string, unknown>;
        hitl_request_id?: string;
      };
      const hitlId = payload.hitl_request_id ?? "";
      const r = hitlId ? resolved.get(hitlId) : undefined;
      blocks.push({
        key: `step-hitl-${s.id}`,
        kind: "hitl",
        toolName: payload.tool_name ?? "",
        toolInput: payload.tool_input ?? {},
        hitlRequestId: hitlId,
        hitlResolved: r !== undefined,
        hitlDecision: r?.decision,
        hitlComment: r?.comment,
        createdAt: s.created_at,
      });
    } else if (s.step_type === "delegation") {
      const payload = s.payload as {
        to_slug?: string;
        task_description?: string;
      };
      blocks.push({
        key: `step-delg-${s.id}`,
        kind: "delegation",
        toSlug: payload.to_slug ?? null,
        taskDescription: payload.task_description ?? null,
        createdAt: s.created_at,
      });
    } else if (s.step_type === "thinking") {
      // Raciocinio persistido: reconstroi o balao no reload (antes so
      // existia ao vivo via SSE). streaming=false -> render estatico.
      const payload = s.payload as { content?: string };
      blocks.push({
        key: `step-think-${s.id}`,
        kind: "thinking",
        content: payload.content ?? "",
        streaming: false,
        createdAt: s.created_at,
      });
    }
    // hitl_resolved isolado nao tem render -- vira flag em hitl_requested.
    // agent_started/agent_completed/error nao tem render dedicado.
  }
  blocks.sort((a, b) => {
    const ta = a.createdAt ?? "";
    const tb = b.createdAt ?? "";
    return ta.localeCompare(tb);
  });
  return blocks;
}

interface ViewProps {
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  onScroll: () => void;
  historyBlocks: RenderBlock[];
  liveBlocks: RenderBlock[];
  streaming: boolean;
  send: (
    text: string,
    attachmentIds?: string[],
    attachmentsPreview?: MessageAttachment[],
  ) => void;
  cancel: () => void;
  decideHitl: (
    hitlRequestId: string,
    payload: {
      decision: "approve" | "reject";
      comment?: string;
      tool_input_override?: Record<string, unknown>;
    },
  ) => Promise<void>;
  /**
   * threadId resolvido (depois de criar a thread em draft mode). Quando
   * vazio em draft mode, o composer chama ``ensureThreadId`` ao tentar
   * anexar para materializar a thread eager.
   */
  composerThreadId?: string;
  /**
   * Cria a thread em draft mode (idempotente). Composer chama no
   * primeiro upload em ``/threads/new``, evitando que o clipe fique
   * desabilitado ate o user enviar a primeira mensagem.
   */
  ensureThreadId: () => Promise<string | null>;
  /**
   * M8: mapa ``llm_model_id -> label`` (ex.: "openai · gpt-4o").
   * Quando um bloco tem ``llmModelId`` set, ``renderBlock`` resolve
   * via esse mapa antes de passar pro MessageBubble. Vazio em
   * direct/team mode (sem snapshot por mensagem).
   */
  modelLabels?: Map<string, string>;
  /** M8: slot a esquerda dos botoes do composer (ex.: ModelSelector). */
  composerLeftSlot?: React.ReactNode;
}

/**
 * Indicador de "digitando": tres pontinhos saltitando. Exibido enquanto o
 * SSE esta aberto e o agente processa MAS ainda nao ha conteudo em streaming
 * -- cobre o vazio entre o envio e o primeiro token (e os gaps entre uma
 * tool e o proximo token / durante a execucao de tools).
 */
function TypingIndicator() {
  return (
    <div
      className="text-ink-500 flex items-center gap-1 px-3 py-2"
      role="status"
      aria-label="O agente está processando"
    >
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
    </div>
  );
}

/**
 * Visibilidade de um bloco conforme o toggle "Mostrar uso de
 * ferramentas". So tool_call/tool_result sao ocultaveis -- HITL, PII,
 * delegacoes, thinking e mensagens sao SEMPRE visiveis (decisao de
 * produto: aprovacoes e avisos nunca podem sumir; delegacao e
 * informacao estrutural do time).
 */
export function isBlockVisible(
  kind: RenderBlock["kind"],
  showToolUsage: boolean,
): boolean {
  if (kind === "tool_call" || kind === "tool_result") {
    return showToolUsage;
  }
  return true;
}

/** IDs de anexo de um tool_result (payload estruturado ou extraidos do
 *  preview). Vazio quando o tool_result nao gerou arquivo. */
export function toolResultAttachmentIds(b: RenderBlock): string[] {
  if (b.attachmentIds && b.attachmentIds.length > 0) return b.attachmentIds;
  return extractAttachmentIdsFromPreview(b.outputPreview ?? "");
}

/**
 * Modo de exibicao de um bloco conforme o toggle "Mostrar uso de
 * ferramentas":
 *  - "full": render completo;
 *  - "files-only": tool_result com o toggle DESLIGADO mas que gerou
 *    arquivo(s) -> mostra so os baloes de arquivo (essenciais ao chat,
 *    nunca somem junto com as tools);
 *  - "hidden": nao renderiza.
 */
export function blockDisplayMode(
  b: RenderBlock,
  showToolUsage: boolean,
): "full" | "files-only" | "hidden" {
  if (showToolUsage) return "full";
  if (b.kind === "tool_call") return "hidden";
  if (b.kind === "tool_result") {
    return toolResultAttachmentIds(b).length > 0 ? "files-only" : "hidden";
  }
  return "full";
}

function ChatStreamView({
  scrollRef,
  onScroll,
  historyBlocks,
  liveBlocks,
  streaming,
  send,
  cancel,
  decideHitl,
  composerThreadId,
  ensureThreadId,
  modelLabels,
  composerLeftSlot,
}: ViewProps) {
  // Pontinhos saltitando: SSE aberto (streaming) e nenhum bloco esta
  // ativamente gerando texto (nenhum ``streaming: true``). Cobre o gap
  // inicial e as pausas entre segmentos/tools.
  const showTyping = streaming && !liveBlocks.some((b) => b.streaming);
  // Filtro de exibicao apenas -- os blocos continuam no estado, entao
  // ligar o toggle no meio da conversa revela os intermediarios
  // retroativamente.
  const showToolUsage = useUiStore((s) => s.chatShowToolUsage);
  // Filtro de exibicao: tool_call/tool_result somem com o toggle off, MAS um
  // tool_result que gerou arquivo vira "files-only" (so os baloes de arquivo),
  // pois relatorios/graficos sao essenciais e nao podem sumir com as tools.
  const renderWithToggle = (b: RenderBlock) => {
    const mode = blockDisplayMode(b, showToolUsage);
    return mode === "hidden"
      ? null
      : renderBlock(b, decideHitl, modelLabels, mode === "files-only");
  };
  return (
    <div className="flex h-full flex-col gap-3">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 space-y-2 overflow-y-auto"
      >
        {historyBlocks.map(renderWithToggle)}
        {liveBlocks.map(renderWithToggle)}
        {showTyping ? <TypingIndicator /> : null}
      </div>
      <MessageComposerInline
        streaming={streaming}
        send={send}
        cancel={cancel}
        threadId={composerThreadId}
        ensureThreadId={ensureThreadId}
        leftSlot={composerLeftSlot}
      />
    </div>
  );
}

export function renderBlock(
  b: RenderBlock,
  decideHitl: ViewProps["decideHitl"],
  modelLabels?: Map<string, string>,
  filesOnly = false,
) {
  if (b.kind === "bubble" || b.kind === "system") {
    const llmModelLabel = b.llmModelId
      ? (modelLabels?.get(b.llmModelId) ?? b.llmModelId.slice(0, 8))
      : undefined;
    return (
      <MessageBubble
        key={b.key}
        role={b.role || "assistant"}
        content={b.content ?? ""}
        streaming={b.streaming}
        agentSlug={b.agentSlug}
        attachments={b.attachments}
        llmModelLabel={llmModelLabel}
      />
    );
  }
  if (b.kind === "thinking") {
    return (
      <ThinkingBlock
        key={b.key}
        content={b.content ?? ""}
        streaming={b.streaming}
      />
    );
  }
  if (b.kind === "tool_call") {
    return (
      <ToolCallBlock
        key={b.key}
        toolName={b.toolName ?? ""}
        args={b.args ?? {}}
      />
    );
  }
  if (b.kind === "tool_result") {
    return (
      <ToolResultBlock
        key={b.key}
        outputPreview={b.outputPreview ?? ""}
        attachmentIds={b.attachmentIds}
        filesOnly={filesOnly}
      />
    );
  }
  if (b.kind === "hitl") {
    const hitlId = b.hitlRequestId ?? "";
    return (
      <HitlBanner
        key={b.key}
        hitlRequestId={hitlId}
        toolName={b.toolName ?? ""}
        toolInput={b.toolInput ?? {}}
        onDecide={(payload) => decideHitl(hitlId, payload)}
        resolved={b.hitlResolved}
        decision={b.hitlDecision}
        decisionComment={b.hitlComment}
      />
    );
  }
  if (b.kind === "delegation") {
    return (
      <DelegationBlock
        key={b.key}
        toSlug={b.toSlug ?? null}
        taskDescription={b.taskDescription ?? null}
      />
    );
  }
  if (b.kind === "pii") {
    return (
      <PiiBadge
        key={b.key}
        direction={b.piiDirection ?? "input"}
        count={b.piiCount ?? 0}
        categories={b.piiCategories ?? []}
      />
    );
  }
  return null;
}

interface ComposerProps {
  streaming: boolean;
  send: (
    text: string,
    attachmentIds?: string[],
    attachmentsPreview?: MessageAttachment[],
  ) => void;
  cancel: () => void;
  threadId?: string;
  /**
   * Em draft mode (sem ``threadId``), o composer chama isto antes do
   * primeiro upload para materializar a thread no servidor.
   * Idempotente: subsequentes retornam o mesmo id.
   */
  ensureThreadId: () => Promise<string | null>;
  /** M8: slot extra na linha dos botoes (ex.: ModelSelector compact). */
  leftSlot?: React.ReactNode;
}

const MAX_PER_MESSAGE = 5;

interface PendingItem {
  key: string;
  file: File;
  uploading: boolean;
  progress: number;
  errorMsg?: string;
  attachment?: Attachment; // populado quando o upload termina
}

function MessageComposerInline({
  streaming,
  send,
  cancel,
  threadId,
  ensureThreadId,
  leftSlot,
}: ComposerProps) {
  const { t } = useTranslation("threads");
  const [text, setText] = useState("");
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const dragCounter = useRef(0);

  // Polling do status real de cada anexo ja upado. O snapshot
  // ``p.attachment`` fica congelado em ``pending``; sem isso o botão
  // Enviar trava se a digestao depois vira failed.
  const pendingAttachmentIds = pending
    .map((p) => p.attachment?.id)
    .filter((x): x is string => Boolean(x));

  const attachmentQueries = useQueries({
    queries: pendingAttachmentIds.map((id) => ({
      queryKey: ["attachment", id] as const,
      queryFn: () => api.GET<Attachment>(`/attachments/${id}`),
      refetchInterval: (query: { state: { data?: Attachment } }) => {
        const data = query.state.data;
        if (!data) return 1500;
        return data.digestion_status === "pending" ||
          data.digestion_status === "processing"
          ? 1500
          : false;
      },
    })),
  });

  const digestionInflight = attachmentQueries.some((q) => {
    const status = q.data?.digestion_status;
    return status === "pending" || status === "processing";
  });

  const anyUploading = pending.some((p) => p.uploading) || digestionInflight;
  const canSend =
    !streaming && !anyUploading && (text.trim() !== "" || pending.length > 0);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      // Em draft mode, ``threadId`` esta vazio. Materializa a thread
      // antes de iniciar o upload -- senao o endpoint POST
      // /threads/{id}/attachments retornaria 404. Idempotente: chamadas
      // subsequentes (segundo arquivo) reusam o mesmo id.
      let activeThreadId = threadId;
      if (!activeThreadId) {
        try {
          const created = await ensureThreadId();
          if (!created) {
            toast.error(t("composer.attachment_requires_thread"));
            return;
          }
          activeThreadId = created;
        } catch {
          toast.error(t("composer.attachment_requires_thread"));
          return;
        }
      }
      const arr = Array.from(files);
      const room = MAX_PER_MESSAGE - pending.length;
      if (arr.length > room) {
        toast.error(t("composer.max_attachments", { max: MAX_PER_MESSAGE }));
        return;
      }
      const next: PendingItem[] = arr.map((f) => ({
        key: `${f.name}-${Date.now()}-${Math.random()}`,
        file: f,
        uploading: true,
        progress: 0,
      }));
      setPending((prev) => [...prev, ...next]);
      for (const item of next) {
        const v = validateFile(item.file);
        if (!v.ok) {
          setPending((prev) =>
            prev.map((p) =>
              p.key === item.key
                ? { ...p, uploading: false, errorMsg: v.error }
                : p,
            ),
          );
          continue;
        }
        try {
          const res = await uploadAttachmentXhr(activeThreadId, item.file, {
            onProgress: (pct) =>
              setPending((prev) =>
                prev.map((p) =>
                  p.key === item.key ? { ...p, progress: pct } : p,
                ),
              ),
          });
          setPending((prev) =>
            prev.map((p) =>
              p.key === item.key
                ? {
                    ...p,
                    uploading: false,
                    progress: 100,
                    attachment: res.body as Attachment,
                  }
                : p,
            ),
          );
        } catch (err: unknown) {
          const errBody =
            err && typeof err === "object" && "body" in err
              ? (err as { body: unknown }).body
              : null;
          const errMsg =
            errBody && typeof errBody === "object" && "error" in errBody
              ? (errBody as { error: { message: string } }).error.message
              : t("composer.upload_failed");
          setPending((prev) =>
            prev.map((p) =>
              p.key === item.key
                ? { ...p, uploading: false, errorMsg: errMsg }
                : p,
            ),
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threadId, pending.length, ensureThreadId],
  );

  function removePending(key: string) {
    setPending((prev) => prev.filter((p) => p.key !== key));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (canSend) doSend();
    }
  }

  function doSend() {
    const successfulAttachments = pending
      .map((p) => p.attachment)
      .filter((a): a is Attachment => Boolean(a));
    const ids = successfulAttachments.map((a) => a.id);
    const preview: MessageAttachment[] = successfulAttachments.map((a) => ({
      id: a.id,
      filename: a.filename,
      mime_type: a.mime_type,
      size_bytes: a.size_bytes,
      digestion_status: a.digestion_status,
      digestion_error: a.digestion_error,
    }));
    send(text, ids, preview);
    setText("");
    setPending([]);
  }

  return (
    <div
      className={cn(
        "relative border-t pt-2",
        isDragging && "ring-brand-400/60 rounded-md ring-2",
      )}
      onDragEnter={(e) => {
        e.preventDefault();
        dragCounter.current += 1;
        if (e.dataTransfer.types.includes("Files")) setIsDragging(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => {
        dragCounter.current -= 1;
        if (dragCounter.current <= 0) {
          dragCounter.current = 0;
          setIsDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragCounter.current = 0;
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) {
          void handleFiles(e.dataTransfer.files);
        }
      }}
    >
      {isDragging ? (
        <div className="bg-brand-50/80 text-brand-800 pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md text-sm font-medium">
          {t("composer.drop_to_attach")}
        </div>
      ) : null}
      {pending.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-2">
          {pending.map((p) => (
            <div
              key={p.key}
              className={cn(
                "border-line bg-surface-0 flex max-w-xs items-center gap-2 rounded-md border p-1.5 pr-1 text-xs",
                p.errorMsg && "border-warning-300 bg-warning-50/60",
              )}
            >
              {p.attachment ? (
                <AttachmentBubble
                  attachmentId={p.attachment.id}
                  filename={p.attachment.filename}
                  mimeType={p.attachment.mime_type}
                  sizeBytes={p.attachment.size_bytes}
                  className="border-0 bg-transparent p-0"
                />
              ) : (
                <div className="flex flex-col">
                  <span className="truncate font-medium">{p.file.name}</span>
                  <span className="text-ink-500 text-[10px]">
                    {p.errorMsg ??
                      (p.uploading
                        ? `${p.progress}%`
                        : formatBytes(p.file.size))}
                  </span>
                </div>
              )}
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removePending(p.key)}
                aria-label={t("composer.remove_attachment")}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t("composer.placeholder")}
        rows={3}
        disabled={streaming}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void handleFiles(e.target.files);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }}
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={streaming || pending.length >= MAX_PER_MESSAGE}
            aria-label={t("composer.attach")}
            title={t("composer.attach")}
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <ShowToolUsageToggle />
          {leftSlot}
        </div>
        {streaming ? (
          <Button variant="outline" onClick={cancel}>
            <StopCircle className="h-4 w-4" />
            {t("composer.stop")}
          </Button>
        ) : (
          <Button
            onClick={doSend}
            disabled={!canSend}
            title={
              anyUploading ? t("composer.wait_uploads") : t("composer.send")
            }
          >
            <Send className="h-4 w-4" />
            {t("composer.send")}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Toggle "Mostrar uso de ferramentas" do composer. Liga/desliga a
 * exibicao dos baloes de tool_call/tool_result (default: escondidos --
 * UI limpa). Preferencia global persistida no uiStore (orbit:ui), vale
 * para chat 1:1, chat de time e sandbox. Le o store direto para nao
 * propagar props pelos 3 usos do composer.
 */
function ShowToolUsageToggle() {
  const { t } = useTranslation("threads");
  const showToolUsage = useUiStore((s) => s.chatShowToolUsage);
  const toggle = useUiStore((s) => s.toggleChatShowToolUsage);
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={showToolUsage}
      title={t("composer.showToolUsage")}
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition-colors",
        showToolUsage
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-muted",
      )}
    >
      <Wrench className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{t("composer.showToolUsage")}</span>
    </button>
  );
}
